import { type FunctionComponent } from 'preact'
import { useRef, useEffect } from 'preact/hooks'
import { signal, computed } from '@preact/signals'
import { html } from 'htm/preact'
import '@substrate-system/button'
import { SubstrateInput } from '@substrate-system/input'
import '@substrate-system/details-summary/css'
import { DetailsSummary } from '@substrate-system/details-summary'
import type { AppState } from '../../state.js'
import { ToolPage, Disclaimer } from '../../components/tool-page.js'
import {
    searchRefrigerants,
    filterRefrigerants,
    idToSlug,
    gwpBarPercent,
    canCompareCount,
    type Refrigerant,
    type RefrigerantCategory,
    type RegulatoryStatus,
} from '../../util/refrigerant-phaseout.js'
import './refrigerant-phaseout.css'
import { NBSP, TIMES } from '../../constants.js'

const CATEGORIES:Array<RefrigerantCategory | ''> = [
    '', 'HCFC', 'HFC', 'HFC Blend', 'HFO', 'HFO/HFC Blend', 'Natural',
]
const STATUSES:Array<RegulatoryStatus | ''> = [
    '', 'Active', 'Phasing Down', 'Phased Out', 'Transitional', 'Future-Proof',
]

const STATUS_LEVEL:Record<RegulatoryStatus, string> = {
    Active: 'low',
    'Future-Proof': 'low',
    Transitional: 'moderate',
    'Phasing Down': 'high',
    'Phased Out': 'critical',
}

const MAX_GWP = Math.max(...filterRefrigerants({}).map(r => r.gwp))

const query = signal('')
const categoryFilter = signal<RefrigerantCategory | ''>('')
const statusFilter = signal<RegulatoryStatus | ''>('')
const compareIds = signal<string[]>([])

const results = computed<Refrigerant[]>(() => {
    const base = searchRefrigerants(query.value)
    const cat = categoryFilter.value
    const st = statusFilter.value
    return base.filter(r => {
        if (cat && r.category !== cat) return false
        if (st && r.status !== st) return false
        return true
    })
})

const compareItems = computed<Refrigerant[]>(() => {
    const ids = compareIds.value
    const all = filterRefrigerants({})
    return ids.map(id => all.find(r => r.id === id)!).filter(Boolean)
})

const canCompare = computed(() => canCompareCount(compareIds.value.length))

function toggleCompare (id:string):void {
    const ids = compareIds.value
    if (ids.includes(id)) {
        compareIds.value = ids.filter(x => x !== id)
    } else if (ids.length < 4) {
        compareIds.value = [...ids, id]
    }
}

const COMPARE_FIELDS:[string, (r:Refrigerant) => string][] = [
    ['Name', r => r.name],
    ['Category', r => r.category],
    ['Status', r => r.status],
    ['GWP', r => String(r.gwp)],
    ['ODP', r => String(r.odp)],
    ['Safety Class', r => r.safetyClass],
    ['Substitutes', r => r.approvedSubstitutes.join(', ') || 'None listed'],
]

export const RefrigerantPhaseout:FunctionComponent<{
    state:AppState
}> = function RefrigerantPhaseout () {
    const items = results.value
    const cmpItems = compareItems.value
    const detailsRef = useRef<HTMLDetailsElement>(null)
    useEffect(() => {
        if (!canCompare.value && detailsRef.current?.open) {
            detailsRef.current.open = false
        }
    }, [compareIds.value])

    return html`
        <${ToolPage}
            title="Refrigerant Phaseout Lookup"
            description="Search and compare refrigerants by phaseout status,
                GWP, safety class, and approved substitutes using EPA Section
                608 reference data."
        >
            <div class="refrigerant-phaseout">
                <${DetailsSummary.TAG} disabled=${!canCompare.value}>
                    <details ref=${detailsRef}>
                        <summary>
                            <span class="compare-summary">
                                <span class="compare-title">
                                    Compare (${cmpItems.length}/4)
                                </span>
                                <span class="compare-chips">
                                    ${cmpItems.map(r => html`
                                        <span
                                            class="compare-chip"
                                            key=${r.id}
                                        >
                                            ${r.name}
                                            <button
                                                type="button"
                                                class="compare-chip-remove"
                                                aria-label=${
                                                    `Remove ${
                                                        r.name
                                                    } from comparison`
                                                }
                                                onClick=${(e:Event) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    toggleCompare(r.id)
                                                }}
                                            >${TIMES}</button>
                                        </span>
                                    `)}
                                </span>
                                ${cmpItems.length > 0 && html`
                                    <button
                                        type="button"
                                        class="compare-clear"
                                        onClick=${(e:Event) => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                            compareIds.value = []
                                        }}
                                    >Clear</button>
                                `}
                            </span>
                        </summary>
                        <div class="details-content">
                            <div class="comparison-panel">
                                <div class="tool-table-wrap">
                                    <table class="tool-table">
                                        <thead>
                                            <tr>
                                                <th>${NBSP}</th>
                                                ${cmpItems.map(r => html`
                                                    <th key=${r.id}>
                                                        ${r.name}
                                                    </th>
                                                `)}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            ${COMPARE_FIELDS.map(
                                                ([label, fn]) => html`
                                                <tr key=${label}>
                                                    <th>${label}</th>
                                                    ${cmpItems.map(
                                                        r => html`
                                                        <td key=${r.id}>
                                                            ${fn(r)}
                                                        </td>
                                                        `
                                                    )}
                                                </tr>
                                                `
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </details>
                </${DetailsSummary.TAG}>
                <div class="phaseout-controls">
                    <div class="search-row">
                        <label
                            class="filter-label"
                            for="refrigerant-search"
                        >
                            Search
                        </label>
                        <${SubstrateInput.TAG}
                            id="refrigerant-search"
                            type="search"
                            placeholder="Search by name or alias (R-22, Puron)"
                            value=${query.value}
                            onInput=${(e:Event) => {
                                query.value =
                                    (e.target as HTMLInputElement).value
                            }}
                        ><//>
                    </div>

                    <div
                        class="filter-group"
                        role="radiogroup"
                        aria-label="Refrigerant type"
                    >
                        <span class="filter-label">Type</span>
                        <div class="pill-row">
                            ${CATEGORIES.map(c => html`
                                <label
                                    class="tool-choice"
                                    key=${c || 'all'}
                                >
                                    <input
                                        type="radio"
                                        name="ref-type"
                                        value=${c}
                                        checked=${
                                            categoryFilter.value === c
                                        }
                                        onChange=${() => {
                                            categoryFilter.value = c
                                        }}
                                    />
                                    ${c === '' ? 'All' : c}
                                </label>
                            `)}
                        </div>
                    </div>

                    <div
                        class="filter-group"
                        role="radiogroup"
                        aria-label="Regulatory status"
                    >
                        <span class="filter-label">Status</span>
                        <div class="pill-row">
                            ${STATUSES.map(s => html`
                                <label
                                    class="tool-choice"
                                    key=${s || 'all'}
                                >
                                    <input
                                        type="radio"
                                        name="ref-status"
                                        value=${s}
                                        checked=${statusFilter.value === s}
                                        onChange=${() => {
                                            statusFilter.value = s
                                        }}
                                    />
                                    ${s === '' ? 'All' : s}
                                </label>
                            `)}
                        </div>
                    </div>

                    <p class="result-count">
                        ${items.length === 1 ?
                            '1 refrigerant found' :
                            `${items.length} refrigerants found`}
                    </p>
                </div>

                ${items.length === 0 && html`
                    <p class="no-results">No refrigerants match your search.</p>
                `}

                <div class="results-list">
                    ${items.map(r => {
                        const width = gwpBarPercent(r.gwp, MAX_GWP)
                        const slug = idToSlug(r.id)
                        const url = `/tools/refrigerant-phaseout/${slug}`
                        return html`
                        <div class="result-row" key=${r.id}>
                            <div class="ref-head">
                                <span class="ref-name">${r.name}</span>
                                <span class="ref-badges">
                                    <span class="tool-badge tool-badge-plain">
                                        ${r.category}
                                    </span>
                                    <span
                                        class="tool-badge"
                                        data-level=${STATUS_LEVEL[r.status]}
                                    >
                                        ${r.status}
                                    </span>
                                </span>
                            </div>

                            <div class="ref-gwp">
                                <div class="ref-gwp-head">
                                    <span class="ref-gwp-label">GWP</span>
                                    <span class="ref-gwp-value">${r.gwp}</span>
                                </div>
                                <div class="gwp-bar" aria-hidden="true">
                                    <div
                                        class="gwp-bar-fill"
                                        style=${`width:${width}%`}
                                    ></div>
                                </div>
                            </div>

                            <p class="ref-summary">${r.summary}</p>

                            <div class="result-actions">
                                <a
                                    class="ref-detail-link"
                                    href=${url}
                                >
                                    Full details →
                                </a>
                                <substrate-button
                                    type="button"
                                    disabled=${
                                        !compareIds.value.includes(r.id) &&
                                        compareIds.value.length >= 4
                                    }
                                    onClick=${(e:Event) => {
                                        e.preventDefault()
                                        toggleCompare(r.id)
                                    }}
                                >
                                    ${compareIds.value.includes(r.id)
                                        ? 'Remove'
                                        : 'Compare'}
                                </substrate-button>
                            </div>
                        </div>
                        `
                    })}
                </div>

                <${Disclaimer} />
            </div>
        </${ToolPage}>
    `
}
